// Plantilla de Excel del Presupuesto: exportar para llenar fuera del sistema e
// importar de vuelta. Vive aparte de la pantalla (sin React) para poder probar
// el viaje de ida y vuelta con tests — ver tests/unit/presupuesto-plantilla.test.ts.

export const MESES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

export interface CuentaPresu {
  cuenta_id: string; codigo: string; nombre: string; tipo: string;
  meses: number[]; real: number[];
}

/** Acepta lo que de verdad escribe la gente en Excel: "1.234,56", "1,234.56",
 *  "$ 1234", "(500)" para negativos, y vacío. */
export const num = (s: string | number | null | undefined): number => {
  if (typeof s === 'number') return Number.isFinite(s) ? s : 0;
  let t = String(s ?? '').replace(/[\s$]/g, '');
  if (!t) return 0;
  const neg = /^\(.*\)$/.test(t);
  if (neg) t = t.slice(1, -1);
  const coma = t.lastIndexOf(',');
  const punto = t.lastIndexOf('.');
  if (coma > -1 && punto > -1) {
    // el separador decimal es el que aparece más a la derecha
    t = coma > punto ? t.replace(/\./g, '').replace(',', '.') : t.replace(/,/g, '');
  } else if (coma > -1) {
    t = t.replace(',', '.');
  }
  const v = Number(t);
  return Number.isFinite(v) ? (neg ? -v : v) : 0;
};

export const suma = (a: number[]) => a.reduce((x, y) => x + Number(y || 0), 0);

/** Normaliza un texto para comparar: sin tildes, sin espacios, minúsculas. */
export const norm = (s: unknown) =>
  String(s ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();

// Cabeceras de la plantilla. La importación las busca POR NOMBRE, así que el
// usuario puede reordenar columnas o añadir las suyas sin romper nada; lo único
// que no debe hacer es renombrar estas.
export const COL_CODIGO = 'Código';
export const COL_CUENTA = 'Cuenta';
export const COL_REAL = 'Real del año (referencia, NO se importa)';

/** Matriz de la plantilla: encabezado + una fila por cuenta. */
export function construirPlantilla(cuentas: CuentaPresu[]): (string | number | null)[][] {
  const encabezado = [COL_CODIGO, COL_CUENTA, ...MESES, COL_REAL];
  const filas = cuentas.map((c) => [
    c.codigo, c.nombre,
    // las celdas en cero se dejan VACÍAS: es más cómodo de llenar y al importar
    // un vacío vale 0 igual
    ...Array.from({ length: 12 }, (_, i) => c.meses[i] || null),
    suma(c.real) || null,
  ]);
  return [encabezado, ...filas];
}

export async function descargarPlantilla(anio: number, universo: string, cuentas: CuentaPresu[]) {
  const XLSX = await import('xlsx');
  const ws = XLSX.utils.aoa_to_sheet(construirPlantilla(cuentas));
  ws['!cols'] = [{ wch: 12 }, { wch: 44 }, ...MESES.map(() => ({ wch: 11 })), { wch: 18 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Presupuesto');
  XLSX.writeFile(wb, `plantilla-presupuesto-${anio}-${universo}.xlsx`);
}

export interface ResultadoImport {
  aplicadas: number; enCero: number; sinCodigo: number; noEncontradas: string[];
}

/** Lee las filas crudas de la hoja y las casa con el plan de cuentas por CÓDIGO.
 *  Separado de la lectura del archivo para poder probarlo sin tocar disco. */
export function interpretarFilas(rows: unknown[][], cuentas: CuentaPresu[]):
  { cambios: Record<string, number[]>; resumen: ResultadoImport } {
  // la fila de encabezado puede no ser la primera (alguien añade un título arriba)
  const iHead = rows.findIndex((r) => Array.isArray(r) && r.some((c) => norm(c) === norm(COL_CODIGO)));
  if (iHead < 0) {
    throw new Error(`No encuentro la columna "${COL_CODIGO}". ¿Es la plantilla descargada de esta pantalla?`);
  }
  const head = rows[iHead].map(norm);
  const cCod = head.indexOf(norm(COL_CODIGO));
  const cMes = MESES.map((m) => head.findIndex((h) => h.slice(0, 3) === norm(m)));
  if (cMes.some((i) => i < 0)) {
    throw new Error(`Faltan columnas de meses en el archivo: ${MESES.filter((_, i) => cMes[i] < 0).join(', ')}`);
  }

  const porCodigo = new Map(cuentas.map((c) => [norm(c.codigo), c]));
  const cambios: Record<string, number[]> = {};
  const resumen: ResultadoImport = { aplicadas: 0, enCero: 0, sinCodigo: 0, noEncontradas: [] };

  for (const r of rows.slice(iHead + 1)) {
    if (!Array.isArray(r)) continue;
    const cod = norm(r[cCod]);
    if (!cod) { resumen.sinCodigo++; continue; }
    const c = porCodigo.get(cod);
    if (!c) {
      if (resumen.noEncontradas.length < 50) resumen.noEncontradas.push(String(r[cCod]));
      continue;
    }
    const meses = cMes.map((i) => Math.round(num(r[i] as string | number) * 100) / 100);
    cambios[c.cuenta_id] = meses;
    if (suma(meses) === 0) resumen.enCero++;
    resumen.aplicadas++;
  }
  return { cambios, resumen };
}

/** Lee el archivo subido. NO guarda nada: el resultado va a la edición pendiente
 *  para que el usuario lo revise antes de pulsar Guardar. */
export async function leerPlantilla(file: File, cuentas: CuentaPresu[]) {
  const XLSX = await import('xlsx');
  const wb = XLSX.read(await file.arrayBuffer(), { type: 'array' });
  const hoja = wb.SheetNames.find((n) => norm(n) === 'presupuesto') ?? wb.SheetNames[0];
  const ws = hoja ? wb.Sheets[hoja] : undefined;
  if (!ws) throw new Error('El archivo no tiene ninguna hoja legible');
  const rows = XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, blankrows: false, raw: true });
  return interpretarFilas(rows, cuentas);
}
